import { reactive, ref, type Ref } from 'vue';
import { http } from '@/interceptors/axiosRequestInterceptor';

/**
 * AB#21807 — Comms mood: the read side of the faces.
 *
 * One small face per order admin for how their communication with customers
 * and factories went (today / last 7 days / last 30 days), the reasons behind it,
 * and a team grid for the people allowed to see it. Two permissions gate it —
 * view-own-communication-mood and view-team-communication-mood — and neither is
 * given to any role by default. The backend answers {enabled:false} while the
 * feature is switched off; every surface then renders nothing.
 *
 * The drawer is one instance for the whole app (mounted by the header), so the
 * top-bar badge, the Needs Attention chip and the team grid all open the same
 * panel through the module-level `drawer` state.
 */

export type MoodLevel = 0 | 1 | 2 | 3 | 4 | 5;
export type MoodPeriod = 'day' | 'week' | 'month';

export const OWN_PERMISSION = 'view-own-communication-mood';
export const TEAM_PERMISSION = 'view-team-communication-mood';

export interface MoodFace {
  level: MoodLevel;
  label: string;
}

export interface MoodReason {
  kind: string;
  severity: number;
  title: string;
  message_id?: number;
  order_id?: number;
  order_item_id?: number;
  order_no?: string;
  channel?: string;
  counterpart?: string;
  visibility?: string;
  at?: string;
  quote?: string;
  why?: string;
  suggestion?: string;
  waited_hours?: number;
}

export interface MoodLegendRow {
  level: MoodLevel;
  label: string;
  meaning: string;
}

export interface MoodDims {
  tone: MoodFace | null;
  answered: MoodFace | null;
  clarity: MoodFace | null;
  /** AB#21991 — Zendesk threads on the person's orders still to confirm; absent or null when not counted. */
  threads?: MoodFace | null;
}

export interface MoodSummary {
  enabled: true;
  subject: { id: number; name: string };
  period: MoodPeriod;
  faces: { today: MoodFace; week: MoodFace; month: MoodFace };
  detail: {
    level: MoodLevel;
    label: string;
    dims: MoodDims;
    counts: Record<string, number | null>;
    reasons: MoodReason[];
    computed_at: string | null;
  };
  legend: MoodLegendRow[];
  sources: string[];
}

export interface TeamMoodPerson {
  user_id: number;
  name: string;
  /** Mon → Sun; null = a day still to come, 0 = no messages. */
  days: (MoodLevel | null)[];
  week: MoodLevel;
  month: MoodLevel;
}

export interface TeamMood {
  enabled: true;
  days: string[];
  people: TeamMoodPerson[];
  faces: { today: MoodFace; week: MoodFace; month: MoodFace };
  legend: MoodLegendRow[];
}

type Off = { enabled: false };

export const MOOD_LABELS: Record<MoodLevel, string> = {
  5: 'Great',
  4: 'Good',
  3: 'Mixed',
  2: 'Needs care',
  1: 'Concern',
  0: 'No messages',
};

export const CHANNEL_LABELS: Record<string, string> = {
  activity_comment: 'Order timeline',
  item_internal_comment: 'Internal comment',
  order_comment: 'Order comments',
  zendesk: 'Zendesk',
};

/** Module-level: one drawer for the app. */
export const drawer = reactive<{
  open: boolean;
  /** 'team' = the team grid; 'person' = one person's mood (own or someone else's). */
  view: 'person' | 'team';
  /** null = the viewer's own mood. */
  subjectId: number | null;
  subjectName: string | null;
  period: MoodPeriod;
  /** A person opened from the team grid can step back to it. */
  fromTeam: boolean;
}>({
  open: false,
  view: 'person',
  subjectId: null,
  subjectName: null,
  period: 'week',
  fromTeam: false,
});

export const openMoodDrawer = (
  opts: {
    subjectId?: number | null;
    subjectName?: string | null;
    period?: MoodPeriod;
  } = {}
): void => {
  drawer.view = 'person';
  drawer.subjectId = opts.subjectId ?? null;
  drawer.subjectName = opts.subjectName ?? null;
  drawer.period = opts.period ?? 'week';
  drawer.fromTeam = false;
  drawer.open = true;
};

/** The team grid, in the same drawer (view-team-communication-mood). */
export const openTeamMoodDrawer = (): void => {
  drawer.view = 'team';
  drawer.subjectId = null;
  drawer.subjectName = null;
  drawer.fromTeam = false;
  drawer.open = true;
};

/** A row in the team grid → that person's detail, with a way back. */
export const openPersonFromTeam = (subjectId: number, subjectName: string): void => {
  drawer.view = 'person';
  drawer.subjectId = subjectId;
  drawer.subjectName = subjectName;
  drawer.period = 'week';
  drawer.fromTeam = true;
};

export const closeMoodDrawer = (): void => {
  drawer.open = false;
};

const isOn = <T>(r: unknown): r is T =>
  !!r && typeof r === 'object' && (r as { enabled?: boolean }).enabled === true;

// The badge and the chip both want `me:week`; share it for a minute so the
// header and the page don't fetch it twice on the same load.
const cache = new Map<string, { at: number; value: MoodSummary | Off | null }>();
const TTL = 60_000;

export const clearMoodCache = (): void => cache.clear();

export async function fetchSummary(
  period: MoodPeriod,
  subjectId: number | null = null
): Promise<MoodSummary | Off | null> {
  const key = `${subjectId ?? 'me'}:${period}`;
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < TTL) return hit.value;

  const url = subjectId === null ? 'comms-mood/me' : `comms-mood/users/${subjectId}`;
  try {
    const { data } = await http.get(url, { params: { period } });
    const r = data?.result ?? null;
    const value: MoodSummary | Off | null = isOn<MoodSummary>(r)
      ? r
      : r && r.enabled === false
        ? { enabled: false }
        : null;
    // Somebody else's drawer is logged server-side on every open; never cache it.
    if (subjectId === null) cache.set(key, { at: Date.now(), value });
    return value;
  } catch {
    // A mood read must never take a page down — the surface just stays quiet.
    return null;
  }
}

export async function fetchTeam(): Promise<TeamMood | Off | null> {
  try {
    const { data } = await http.get('comms-mood/team');
    const r = data?.result ?? null;
    return isOn<TeamMood>(r) ? r : r && r.enabled === false ? { enabled: false } : null;
  } catch {
    return null;
  }
}

/** AB#22134 — the CSM team's grid: CSM team members only (view-csm-team-communication-mood). */
export const CSM_TEAM_MOOD_PERMISSION = 'view-csm-team-communication-mood';
/** AB#22175 — or the CSM EU team's (the same permission AND the CSM EU team overview). */
export async function fetchCsmTeamMood(
  path: 'comms-mood/csm-team' | 'comms-mood/csm-eu-team' = 'comms-mood/csm-team'
): Promise<TeamMood | Off | null> {
  try {
    const { data } = await http.get(path);
    const r = data?.result ?? null;
    return isOn<TeamMood>(r) ? r : r && r.enabled === false ? { enabled: false } : null;
  } catch {
    return null;
  }
}

export async function flagJudgement(messageId: number, note: string | null): Promise<boolean> {
  try {
    const { data } = await http.post('comms-mood/flags', { message_id: messageId, note });
    return data?.result?.flagged === true;
  } catch {
    return false;
  }
}

/** The viewer's own faces for the badge / chip. */
export function useOwnMood(): {
  summary: Ref<MoodSummary | null>;
  loading: Ref<boolean>;
  load: () => Promise<void>;
} {
  const summary = ref<MoodSummary | null>(null);
  const loading = ref(false);

  const load = async (): Promise<void> => {
    loading.value = true;
    const r = await fetchSummary('week');
    summary.value = r && r.enabled ? r : null;
    loading.value = false;
  };

  return { summary, loading, load };
}

/** Everything that is not "good" — used to pick the badge's attention dot. */
/**
 * Message text as a person reads it: HTML entities decoded (&nbsp; &amp;
 * &#39; …), non-breaking spaces as spaces, runs of spaces collapsed. Parsed
 * as inert text (DOMParser runs no scripts and the result is only ever
 * rendered with {{ }}), so a quote can never inject markup.
 */
export const plainText = (value: string | null | undefined): string => {
  if (!value) return '';
  let text = value;
  if (text.includes('&') && typeof DOMParser !== 'undefined') {
    text = new DOMParser().parseFromString(text, 'text/html').documentElement.textContent ?? text;
  }
  return text
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
};

export const needsLook = (level: MoodLevel): boolean => level >= 1 && level <= 3;
